import type { Lot } from '../types/lot'
import type { MortalityLog } from '../types/production'
import { farmDayKey, farmTimeZone } from './farmTime'

// v2.2 findings #74 — "días sin registro". ASC 4.1.1.10.4, BAP (daily
// mortalities) and GLOBALG.A.P. AQ-GFS 20.02.01 / 20.05.02 ask for mortality
// recorded daily per production unit. A day with no record is a gap, not a
// zero, so this reports completeness next to the mortality figures instead of
// changing any of them:
// - "registrado": at least one mortality record that day, including an
//   explicit "Sin mortalidad" zero;
// - "no revisado": only "No se pudo revisar" records that day (ASC reason);
// - "sin registro": nothing at all.
// Pure: one row per lot and ISO week, days counted in the lot's active span
// (stocking or first record → harvest / last record, or today while Active).

export type MortalityRecordingRow = {
  id: string
  farmId: string
  productionUnitId: string
  lotId: string
  lot: string
  week: string
  date: string
  daysInPeriod: number
  daysRecorded: number
  daysNoMortality: number
  daysNotChecked: number
  daysWithoutRecord: number
}

const DAY_MS = 24 * 60 * 60 * 1000

// Calendar arithmetic on a local Date built from a day key (the loop below).
const localDayKey = (date: Date): string | null => {
  if (Number.isNaN(date.getTime())) return null
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

// Findings #85: a stored value's day is the lot's FARM day — a calendar-day
// value (stocking date, UTC-midnight sentinel) keeps its date; a crew record's
// instant is read in the farm's zone, not the viewer's.
const farmDay = (value: string, timeZone: string): string | null => farmDayKey(value, timeZone)

const dayKeyToDate = (key: string): Date => {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(y, m - 1, d)
}

/** ISO-8601 week label, e.g. "2026-W40", for a local calendar day. */
export const isoWeekOf = (day: Date): string => {
  const date = new Date(Date.UTC(day.getFullYear(), day.getMonth(), day.getDate()))
  const weekday = date.getUTCDay() || 7
  date.setUTCDate(date.getUTCDate() + 4 - weekday)
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1))
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7)
  return `${date.getUTCFullYear()}-W${String(week).padStart(2, '0')}`
}

type DayStatus = 'recorded' | 'noMortality' | 'notChecked'

export const buildMortalityRecordingRows = (args: {
  lots: Lot[]
  mortalityLogs: MortalityLog[]
  today?: Date
}): MortalityRecordingRow[] => {
  const today = args.today ?? new Date()

  const logsByLot = new Map<string, MortalityLog[]>()
  for (const log of args.mortalityLogs) {
    if (!log.lotId) continue
    const list = logsByLot.get(log.lotId) ?? []
    list.push(log)
    logsByLot.set(log.lotId, list)
  }

  const rows: MortalityRecordingRow[] = []
  for (const lot of args.lots) {
    if (!lot.productionUnitId) continue // facility-level arrival lots hold no fish
    const logs = logsByLot.get(lot.id) ?? []
    const zone = farmTimeZone(lot.farmId)
    const todayKey = farmDayKey(today, zone) as string

    // Per-day status: any real record wins over "no revisado".
    const statusByDay = new Map<string, DayStatus>()
    for (const log of logs) {
      const key = farmDay(log.recordedAt, zone)
      if (!key) continue
      const metadata = (log.metadata ?? {}) as Record<string, unknown>
      const status: DayStatus = metadata.notChecked === true
        ? 'notChecked'
        : metadata.noMortality === true
          ? 'noMortality'
          : 'recorded'
      const previous = statusByDay.get(key)
      if (!previous || previous === 'notChecked' || (previous === 'noMortality' && status === 'recorded')) {
        statusByDay.set(key, status)
      }
    }

    const logDays = Array.from(statusByDay.keys()).sort()
    const startKey = (lot.stockingDate ? farmDay(lot.stockingDate, zone) : null) ?? logDays[0]
    if (!startKey) continue
    const isActive = !lot.stage || lot.stage === 'Active'
    const endKey = isActive
      ? todayKey
      : (lot.harvestDate ? farmDay(lot.harvestDate, zone) : null) ?? logDays[logDays.length - 1] ?? startKey
    if (endKey < startKey) continue

    const byWeek = new Map<string, MortalityRecordingRow>()
    for (let day = dayKeyToDate(startKey); localDayKey(day)! <= endKey; day = new Date(day.getTime() + DAY_MS)) {
      const key = localDayKey(day) as string
      const week = isoWeekOf(day)
      let row = byWeek.get(week)
      if (!row) {
        row = {
          id: `${lot.id}:${week}`,
          farmId: lot.farmId,
          productionUnitId: lot.productionUnitId,
          lotId: lot.id,
          lot: lot.lotKey,
          week,
          date: key,
          daysInPeriod: 0,
          daysRecorded: 0,
          daysNoMortality: 0,
          daysNotChecked: 0,
          daysWithoutRecord: 0
        }
        byWeek.set(week, row)
      }
      row.daysInPeriod += 1
      const status = statusByDay.get(key)
      if (status === 'notChecked') row.daysNotChecked += 1
      else if (status) {
        row.daysRecorded += 1
        if (status === 'noMortality') row.daysNoMortality += 1
      } else row.daysWithoutRecord += 1
    }
    rows.push(...byWeek.values())
  }
  return rows
}
